import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { countPodiums, withCategoryStanding } from '../../models/podium.models';
import { Component, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { HumanService } from '../../services/human.service';
import { ResultService } from '../../services/result.service';
import { SearchService } from '../../services/search.service';
import { Human, Result, Race, Event } from '../../models/data.models';
import { FormsModule } from '@angular/forms';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { HumanAvatarComponent } from '../../components/human-avatar/human-avatar.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';

interface CombinedStats {
  totalRaces: number;
  totalDistance: number;
  averagePosition: number | null;
  averageSpeed: number | null;
  bestRank: number | null;
  maxSpeed: number | null;
  podiums: { first: number; second: number; third: number };
}

/**
 * Winner of a shared race: 1, 2, or 0 when neither can be declared. A result without a positive
 * time or with a status (DNF, DSQ…) did not finish, so it never beats a finisher.
 */
export function headToHeadWinner(
  first: Pick<Result, 'timeSeconds' | 'statusCode'>,
  second: Pick<Result, 'timeSeconds' | 'statusCode'>,
): 0 | 1 | 2 {
  const finished = (result: Pick<Result, 'timeSeconds' | 'statusCode'>) =>
    !result.statusCode && (result.timeSeconds ?? 0) > 0;
  const firstFinished = finished(first);
  const secondFinished = finished(second);
  if (firstFinished && secondFinished) {
    if (first.timeSeconds < second.timeSeconds) return 1;
    if (second.timeSeconds < first.timeSeconds) return 2;
    return 0;
  }
  if (firstFinished) return 1;
  if (secondFinished) return 2;
  return 0;
}

@Component({
  selector: 'app-compare-profiles',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, RouterModule, FormsModule, DisplayValuePipe, DistancePipe, TranslatePipe, HumanAvatarComponent, PageHeaderComponent],
  templateUrl: './compare-profiles.component.html',
})
export class CompareProfilesComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private humanService = inject(HumanService);
  private resultService = inject(ResultService);
  private searchService = inject(SearchService);
  private translationService = inject(TranslationService);

  // States
  human1 = signal<Human | null>(null);
  human2 = signal<Human | null>(null);
  results1 = signal<(Result & { race: Race })[]>([]);
  results2 = signal<(Result & { race: Race })[]>([]);
  isLoading1 = signal(false);
  isLoading2 = signal(false);

  // Search states
  searchQuery = signal('');
  searchResults = signal<Human[]>([]);
  isSearching = signal(false);
  activeSlot = signal<1 | 2>(1);
  showSearchOverlay = signal(false);
  isSelectionMode = signal(false);

  constructor() {
    // Initial load from URL
    const id1 = this.route.snapshot.paramMap.get('id1');
    const id2 = this.route.snapshot.paramMap.get('id2');

    if (id1) void this.loadProfile(id1, 1);
    if (id2) void this.loadProfile(id2, 2);

    if (id1 && !id2) {
      this.activeSlot.set(2);
      this.showSearchOverlay.set(true);
    } else if (!id1) {
      this.activeSlot.set(1);
      this.showSearchOverlay.set(true);
    }
  }

  private loadVersions = { 1: 0, 2: 0 };

  async loadProfile(id: string, slot: 1 | 2) {
    const human = slot === 1 ? this.human1 : this.human2;
    const results = slot === 1 ? this.results1 : this.results2;
    const isLoading = slot === 1 ? this.isLoading1 : this.isLoading2;
    // A slot can be re-picked before its previous profile finished loading: the latest pick wins.
    const version = ++this.loadVersions[slot];
    const isCurrent = () => version === this.loadVersions[slot];

    isLoading.set(true);
    results.set([]);
    try {
      const h = await this.humanService.getHuman(id);
      if (!isCurrent()) return;
      human.set(h);
      if (h) {
        const ranked = await this.rankResults(await this.resultService.getResultsByHuman(id));
        if (isCurrent()) results.set(ranked);
      }
    } catch (error) {
      console.error('Error loading compared profile:', error);
    } finally {
      if (isCurrent()) isLoading.set(false);
    }
  }

  private async rankResults(results: (Result & { race: Race })[]) {
    const standings = await this.resultService.getCategoryStandings([...new Set(results.map(result => result.raceId))]);
    return results.map(result => withCategoryStanding(result, standings[result.id]));
  }

  getStats(results: (Result & { race: Race })[]): CombinedStats {
    const validResults = results.filter(r => r.timeSeconds > 0);
    const ranks = validResults.map(r => r.rank).filter((r): r is number => r !== undefined && r !== null && r > 0);
    const positions = results
      .map(r => r.position)
      .filter((position): position is number => position !== undefined && position !== null && position > 0);
    const speeds = validResults.map(r => r.averageSpeedKmph).filter((s): s is number => s !== undefined && s !== null && s > 0);
    const speedResults = validResults.filter(r => (r.race?.distanceKm || 0) > 0);
    const speedDistance = speedResults.reduce((total, result) => total + result.race.distanceKm, 0);
    const speedTime = speedResults.reduce((total, result) => total + result.timeSeconds, 0);
    const calculatedAverageSpeed = speedDistance > 0 && speedTime > 0
      ? speedDistance / (speedTime / 3600)
      : null;

    return {
      totalRaces: results.length,
      totalDistance: results.reduce((acc, curr) => acc + (curr.race?.distanceKm || 0), 0),
      averagePosition: positions.length > 0
        ? positions.reduce((total, position) => total + position, 0) / positions.length
        : null,
      averageSpeed: calculatedAverageSpeed ?? (speeds.length > 0
        ? speeds.reduce((total, speed) => total + speed, 0) / speeds.length
        : null),
      bestRank: ranks.length > 0 ? Math.min(...ranks) : null,
      maxSpeed: speeds.length > 0 ? Math.max(...speeds) : null,
      podiums: countPodiums(results)
    };
  }

  stats1 = computed(() => this.getStats(this.results1()));
  stats2 = computed(() => this.getStats(this.results2()));

  faceToFace = computed(() => {
    const races1 = new Map<string, Result & { race: Race }>();
    this.results1().forEach(r => { if (r.raceId) races1.set(r.raceId, r); });

    const shared = [];
    for (const r2 of this.results2()) {
      if (r2.raceId && races1.has(r2.raceId)) {
        const r1 = races1.get(r2.raceId)!;
        shared.push({
          race: r1.race,
          event: r1.event,
          rank1: r1.rank,
          rank2: r2.rank,
          time1: r1.timeSeconds,
          time2: r2.timeSeconds,
          winner: headToHeadWinner(r1, r2)
        });
      }
    }
    return shared.sort((a, b) => new Date(b.event?.date || 0).getTime() - new Date(a.event?.date || 0).getTime());
  });

  h2hScore = computed(() => {
    let score1 = 0;
    let score2 = 0;
    this.faceToFace().forEach(m => {
      if (m.winner === 1) score1++;
      if (m.winner === 2) score2++;
    });
    return { score1, score2 };
  });

  async onSearch() {
    const q = this.searchQuery();
    if (q.length < 2) {
      this.searchResults.set([]);
      return;
    }

    this.isSearching.set(true);
    try {
      const results = await this.searchService.search(q);
      if (q !== this.searchQuery()) return;
      const selectedIds = new Set([this.human1()?.id, this.human2()?.id].filter(Boolean));
      // Homonyms are distinct runners: keep every profile, only drop the ones already compared.
      this.searchResults.set(results.humans.filter(human => !selectedIds.has(human.id)));
    } catch (error) {
      console.error('Error searching profiles to compare:', error);
      this.searchResults.set([]);
    } finally {
      this.isSearching.set(false);
    }
  }

  selectProfile(human: Human) {
    const slot = this.activeSlot();
    void this.loadProfile(human.id, slot);
    this.searchResults.set([]);
    this.searchQuery.set('');
    this.showSearchOverlay.set(false);
    this.isSelectionMode.set(false);

    const id1 = slot === 1 ? human.id : this.human1()?.id;
    const id2 = slot === 2 ? human.id : this.human2()?.id;
    if (id1 && id2) void this.router.navigate(['/compare/human', id1, id2], { replaceUrl: true });
    else if (id1) void this.router.navigate(['/compare/human', id1], { replaceUrl: true });
  }

  openSearch(slot: 1 | 2) {
    this.activeSlot.set(slot);
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.showSearchOverlay.set(true);
    this.isSelectionMode.set(true);
  }

  closeSearch() {
    this.showSearchOverlay.set(false);
    this.isSelectionMode.set(false);
  }

  formatTime(totalSeconds: number): string {
    if (!totalSeconds) return '--';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  formatMonth(date: string | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString(this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });
  }

  getFlagEmoji(nat: string | undefined) {
    return this.humanService.getFlagEmoji(nat);
  }
}
